/* ALT bundle PDP: native-personalizer engine host (bundle PDP redesign, B3/B6/B7).
   Defines <alt-bundle-product>, the alt-engine counterpart to assets/custom.js's <product-bundle>.

   This element owns the whole add-to-cart contract for an alt-engine bundle: size wiring, the
   submit flow (validation, item building, the atomic rush check, the /cart/add.js POST and its
   success/error handling), and resetting every child <native-personalizer> after a successful add.
   It is the ONLY thing that calls getConfirmedProperties()/isEditing()/resetAfterBundleAdd() on
   its child personalizers; assets/alt-bundle-picker.js only ever READS their confirmed state (via
   getConfirmedProperties()) to drive the per-tile status pill, never drives the add flow itself.

   Never touches assets/custom.js or assets/product.js (frozen, repo CLAUDE.md); never edits
   assets/native-personalizer.js (its BUNDLE MODE (B2) public API is the frozen contract this file
   calls against: bindBundleVariantListener's `alt-bundle:variant-change` event, applyAvailability,
   isEditing(), getConfirmedProperties(), resetAfterBundleAdd()). Markup contract lives in
   snippets/product-bundle-components.liquid's is_alt branch.

   Ported 1:1 from the legacy engine (assets/custom.js:601-731, ProductBundle) where the task calls
   for it, with three additions the legacy engine does not have: an in-flight guard (legacy has
   none — a double click can double-post), the __bundleengine:"alt" marker on every component line,
   and the atomic-add cleanup on a failed POST (legacy never rolls back a partial add).
*/
(function () {
  'use strict';

  if (customElements.get('alt-bundle-product')) return;

  // Same filter custom.js:614 uses: the component-data <script> is the only child whose `.type`
  // reads 'application/json' (an unset .type on any other element is never that exact string), so
  // this is safe regardless of tile type (1/2/3/4) or which markup a tile renders around it.
  function componentData(bundleComponent) {
    var script = Array.prototype.filter.call(bundleComponent.children, function (child) {
      return child.type === 'application/json';
    })[0];
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      return null;
    }
  }

  class AltBundleProduct extends HTMLElement {
    connectedCallback() {
      // Guard against a reconnect (section re-render, DOM move) stacking duplicate listeners,
      // same pattern native-personalizer.js uses for its own once-per-instance wiring.
      if (this._wired) return;
      this._wired = true;

      this._submitting = false;
      this.moneyTemplate(); // capture the server-rendered money format before any live update rewrites it
      this.tileMoneyTemplate();
      this.bundleForm = this.querySelector('form[id^="product__bundle-form__"]');
      this.errorEl = this.querySelector('.alt-bundle-error');

      this.bindSizeWiring();

      // Finding #6: every price surface (tile price/badge, bundle total/strike, Afterpay amount,
      // the data-original-price/data-discounted-price attrs buildItems() reads into the cart
      // properties) must track whatever variant is actually selected, not just the
      // server-rendered first/default variant. alt-bundle:variant-change bubbles from whichever
      // <bundle-component> changed up to this element (bindSizeWiring's click handler above, and
      // the programmatic setTileVariant() the B8 edit-prefill flow below also uses), so one
      // listener here covers every path that can change a selection.
      this.addEventListener('alt-bundle:variant-change', () => this.recomputeBundlePrices());

      if (this.bundleForm) {
        this.bundleForm.addEventListener('submit', this.onSubmit.bind(this));
      }

      // B8: cart "Edit personalization" link landing. Fire-and-forget, same discipline
      // native-personalizer.js's own initEditPrefill() documents: every failure path leaves a
      // plain, unmodified bundle PDP.
      this.initBundleEditFromUrl();
    }

    // Ported from assets/custom.js:611-631 (ProductBundle.connectedCallback), plus: sets
    // data-selected-variant-available (native-personalizer.js's applyAvailability signal for
    // bundle mode reads exactly this attribute, see its `_boundBundleComponent.dataset
    // .selectedVariantAvailable` check) and dispatches `alt-bundle:variant-change` — NEVER
    // `theme:variant:change`, which many page-level listeners (rush-personalization-availability.js
    // among them) react to and which has no per-component meaning on a bundle page.
    bindSizeWiring() {
      this.querySelectorAll('bundle-component').forEach((bundleComponent) => {
        var bundleProduct = componentData(bundleComponent);
        if (!bundleProduct || !bundleProduct.variants) return;
        var unavailableOptions = bundleProduct.variants.filter((variant) => !variant.available).map((variant) => variant.title);

        bundleComponent.querySelectorAll('[data-single-option-selector]').forEach((option) => {
          if (unavailableOptions.indexOf(option.value) !== -1) {
            option.classList.add('sold-out');
            option.removeAttribute('checked');
            option.setAttribute('disabled', true);
          }

          option.addEventListener('click', (e) => {
            var variant = bundleProduct.variants.find((v) => v.title === e.target.value);
            if (!variant) return;

            bundleComponent.setAttribute('data-selected-variant-id', variant.id);
            bundleComponent.setAttribute('data-selected-variant-available', String(!!variant.available));

            var legendValue = bundleComponent.querySelector('.radio__legend__value');
            if (legendValue) legendValue.innerText = e.target.value;

            bundleComponent.dispatchEvent(
              new CustomEvent('alt-bundle:variant-change', {
                bubbles: true,
                detail: { variant: variant }
              })
            );
          });
        });
      });
    }

    // Every locale string this element needs lives on data-* attributes rendered by
    // snippets/product-bundle-components.liquid (is_alt branch only), same convention
    // assets/alt-bundle-picker.js already uses (container.dataset.alt*Text). Hardcoded fallbacks
    // match those attributes' own defaults so a missing attribute degrades, never breaks.
    strings() {
      return {
        midEdit: this.dataset.altMidEditText || 'Please confirm or cancel your personalization first.',
        errorGeneric: this.dataset.altErrorGenericText || 'Something went wrong adding this bundle to your bag. Please try again.',
        errorNetwork: this.dataset.altErrorNetworkText || "We couldn't reach the cart. Please check your connection and try again.",
        // B8/B9 (cart edit-link landing, whole-bundle): all read from data-* attributes rendered by
        // snippets/product-bundle-components.liquid, same convention as the three above. editEditing
        // no longer takes a %TITLE% token (2026-09-25, B9): the banner now covers the whole edit
        // session, not one line, so it names no single item.
        editEditing:
          this.dataset.altEditEditingText ||
          "You're editing this bundle in your bag. Changes save when you confirm each personalization.",
        editDone: this.dataset.altEditDoneText || 'Done',
        backToBag: this.dataset.altBackToBagText || 'Back to bag',
        savedConfirmation: this.dataset.altSavedConfirmationText || 'Saved to your bag.',
        editNotFound: this.dataset.altEditNotFoundText || 'That item is no longer in your bag.',
        editSizeLocked: this.dataset.altEditSizeLockedText || 'To change the size, remove the bundle from your bag and add it again.',
        editSaveError: this.dataset.altEditSaveErrorText || "We couldn't save your changes. Please try again.",
        editUnavailable: this.dataset.altEditUnavailableText || "We couldn't load this item's personalization to edit here. You can remove the bundle from your bag and add it again.",
        // Finding #1 (failed-add cleanup) and #9 (rejected rush removal): same data-* convention
        // as every string above.
        cleanupIncomplete:
          this.dataset.altCleanupIncompleteText || 'Some items from this bundle may still be in your bag. Please check before continuing.',
        rushRemovalFailed: this.dataset.altRushRemovalFailedText || "We couldn't remove the rush fee. Please check your bag."
      };
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    setBusy(busy, submitBtn) {
      this._submitting = busy;
      if (!submitBtn) return;
      submitBtn.disabled = busy;
      if (busy) {
        submitBtn.setAttribute('aria-busy', 'true');
      } else {
        submitBtn.removeAttribute('aria-busy');
      }
    }

    // Every <native-personalizer> that is unavailable, unconfigured, or never opened simply
    // reports isEditing() === false, so this is safe to call across the whole element regardless
    // of which tiles are personalizable.
    anyPersonalizerEditing() {
      var personalizers = this.querySelectorAll('native-personalizer');
      for (var i = 0; i < personalizers.length; i++) {
        var np = personalizers[i];
        if (typeof np.isEditing === 'function' && np.isEditing()) return true;
      }
      return false;
    }

    // The rush checkbox is resolved generically (querySelector('input[type="checkbox"]')), never
    // through a cached reference: same reasoning native-personalizer.js documents at length for
    // its own `rushCheckbox` getter (an element upgraded before its children parsed would cache
    // null for life). rush-personalization.liquid never names or classes this input beyond that.
    // A dormant element (Rush moved to the bag, 2026-09-30) reads as no checkbox: the bundle never
    // posts a Rush line or a `Rush: Yes` stamp from the PDP.
    get rushCheckbox() {
      var rush = this.querySelector('rush-personalization');
      if (!rush || rush.hasAttribute('data-rush-dormant')) return null;
      return rush.querySelector('input[type="checkbox"]');
    }

    // Builds the cart items for every <bundle-component> in this bundle (all tile types,
    // including the static "Included" type-3 rows, which already carry a server-rendered
    // data-selected-variant-id and must still add to cart — same as custom.js:652 iterating ALL
    // bundle-component children with no tile-type filter). Mirrors assets/custom.js:652-695
    // exactly for the base bundle properties; adds __bundleengine:"alt" to every component line
    // (never to the rush line, which stays bare per its documented contract) and reads
    // personalization from getConfirmedProperties() instead of ProductPersonalization's
    // personalizationValues/validate().
    buildItems() {
      var bundleDiscount = parseInt(this.dataset.bundleDiscount, 10);
      var items = [];
      var bundleAddId = Math.floor(100000 + Math.random() * 900000);
      var rushChecked = !!(this.rushCheckbox && this.rushCheckbox.checked);

      this.querySelectorAll('bundle-component').forEach((bundleComponent) => {
        var variantId = bundleComponent.getAttribute('data-selected-variant-id');
        if (!variantId) return;

        var itemProperties = {
          __bundle: 'true',
          __bundlediscount: bundleDiscount,
          __bundleproduct: this.dataset.bundleHandle,
          __bundleaddid: bundleAddId,
          __bundleoriginalprice: this.dataset.originalPrice,
          __bundlediscountedprice: this.dataset.discountedPrice,
          __bundleengine: 'alt'
        };

        var personalizer = bundleComponent.querySelector('native-personalizer');
        var confirmed = {};
        if (personalizer && typeof personalizer.getConfirmedProperties === 'function') {
          confirmed = personalizer.getConfirmedProperties() || {};
        }
        Object.assign(itemProperties, confirmed);

        if (rushChecked && Object.keys(confirmed).length > 0) {
          itemProperties.Rush = 'Yes';
        }

        items.push({
          id: variantId,
          quantity: 1,
          properties: itemProperties
        });
      });

      return { items: items, bundleAddId: bundleAddId };
    }

    // Finding #6. Percent/eligibility come from data-bundle-discount (rendered by
    // snippets/alt-bundle-price.liquid's 'attrs' mode; 0 means not eligible), never re-derived
    // client-side. Per-component prices come from each component's own rendered variant JSON
    // (componentData(), already used by bindSizeWiring()/buildItems() above), never a network
    // call. Same floor-the-discount-amount formula as the Liquid fix (finding #5):
    // discount_amount = floor(price * percent / 100); discounted = price - discount_amount.
    recomputeBundlePrices() {
      var percent = parseInt(this.dataset.bundleDiscount, 10) || 0;
      var eligible = percent > 0;
      var sum = 0;
      var shownTotal = 0;

      this.querySelectorAll('bundle-component').forEach((bundleComponent) => {
        var bundleProduct = componentData(bundleComponent);
        var variantId = bundleComponent.getAttribute('data-selected-variant-id');
        if (!bundleProduct || !bundleProduct.variants || !variantId) return;
        var variant = bundleProduct.variants.find((v) => String(v.id) === String(variantId));
        if (!variant || typeof variant.price !== 'number') return;

        var price = variant.price;
        var discounted = price;
        if (eligible) {
          var discountAmount = Math.floor((price * percent) / 100);
          discounted = price - discountAmount;
        }
        sum += price;
        shownTotal += discounted;

        this.updateTilePriceUI(bundleComponent, price, discounted, eligible);
      });

      this.dataset.originalPrice = String(sum);
      this.dataset.discountedPrice = String(shownTotal);
      this.updateBundleTotalUI(sum, shownTotal, eligible);
      this.updateAfterpayUI(shownTotal);
    }

    // Money format is COPIED from a price the server already rendered on this page (the bundle
    // total, e.g. "USD $239.00" in the US or "AUD $365.00" in AU), never from
    // window.theme.moneyWithCurrencyFormat: on this store that global is "${{amount_no_decimals}}
    // USD" (whole-dollar display for normal products) and ignores the visitor's market, so it
    // printed "$203 USD" for $203.15 on the staging bundle PDP (2026-09-25). Liquid's
    // money_with_currency, which rendered every server price here, includes cents and the market
    // currency; mirroring its rendered text keeps the live update identical to the server output.
    moneyTemplate() {
      if (this._moneyTemplate) return this._moneyTemplate;
      var block = this.closest('.product__block') || this;
      var sample = block.querySelector('.product__price__wrap [data-compare-price], .product__price__wrap [data-product-price], .alt-bundle-tile__price-strike, .alt-bundle-tile__price');
      var text = sample ? sample.textContent.trim() : '';
      var match = /^(.*?)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(text);
      this._moneyTemplate = match ? { prefix: match[1], suffix: match[3] } : { prefix: 'USD $', suffix: '' };
      return this._moneyTemplate;
    }

    // Tile prices render with plain `money` (no currency code) while the bundle total keeps
    // money_with_currency, so tiles need their own captured template. Sampled per element in
    // priority order (not one selector list: .alt-bundle-tile__price precedes its own children in
    // document order and its text also holds the visually-hidden "Regular price" label).
    tileMoneyTemplate() {
      if (this._tileMoneyTemplate) return this._tileMoneyTemplate;
      var sample = this.querySelector('.alt-bundle-tile__price-strike') ||
        this.querySelector('.alt-bundle-tile__price-sale') ||
        this.querySelector('.alt-bundle-tile__price');
      var text = sample ? sample.textContent.trim() : '';
      var match = /^(.*?)(\d[\d,]*(?:\.\d+)?)(.*)$/.exec(text);
      this._tileMoneyTemplate = match ? { prefix: match[1], suffix: match[3] } : { prefix: '$', suffix: '' };
      return this._tileMoneyTemplate;
    }

    formatMoney(cents, template) {
      var t = template || this.moneyTemplate();
      var parts = (cents / 100).toFixed(2).split('.');
      parts[0] = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
      return t.prefix + parts.join('.') + t.suffix;
    }

    // Updates one tile's price/strike in place. Badge text ("Save X%") never changes per
    // component (percent is bundle-wide), so it's left alone. Two possible rendered shapes
    // (snippets/product-bundle-components.liquid): sale+strike spans (show_item_discount on AND
    // eligible) or a single plain-text price (show_item_discount off, or not eligible) — updates
    // whichever is actually present rather than assuming one.
    updateTilePriceUI(bundleComponent, price, discounted, eligible) {
      var priceEl = bundleComponent.querySelector('.alt-bundle-tile__price');
      if (!priceEl) return;
      var saleEl = priceEl.querySelector('.alt-bundle-tile__price-sale');
      var strikeEl = priceEl.querySelector('.alt-bundle-tile__price-strike');
      if (eligible && saleEl && strikeEl) {
        saleEl.textContent = this.formatMoney(discounted, this.tileMoneyTemplate());
        strikeEl.textContent = this.formatMoney(price, this.tileMoneyTemplate());
      } else if (!saleEl) {
        priceEl.textContent = this.formatMoney(price, this.tileMoneyTemplate());
      }
    }

    // Updates the bundle total (snippets/alt-bundle-price.liquid mode:'pdp'). Eligibility is
    // fixed for the whole page load (it depends on the product/tags, never the selected variant),
    // so only ONE of these two rendered shapes ever exists at once; this reads whichever is there.
    updateBundleTotalUI(sum, shownTotal, eligible) {
      var scope = this.closest('.product__block') || document;
      var priceWrapper = scope.querySelector('[data-price-wrapper]');
      if (!priceWrapper) return;
      if (eligible) {
        var saleEl = priceWrapper.querySelector('.product__price--sale[data-product-price]');
        var strikeEl = priceWrapper.querySelector('[data-compare-price]');
        if (saleEl) saleEl.textContent = this.formatMoney(shownTotal);
        if (strikeEl) strikeEl.textContent = this.formatMoney(sum);
      } else {
        var plainEl = priceWrapper.querySelector('[data-product-price]');
        if (plainEl) plainEl.textContent = this.formatMoney(sum);
      }
    }

    // Updates the Afterpay/Shop Pay messaging amount (snippets/alt-bundle-price.liquid
    // mode:'afterpay'). That render sits OUTSIDE this element (a sibling under
    // .alt-bundle-afterpay, product-bundle-components.liquid), hence the .product__block scope
    // rather than `this`. data-amount is decimal dollars (verified live: "203.15" for 20315
    // cents), not the cents integer the rest of this file uses everywhere else.
    updateAfterpayUI(shownTotal) {
      var scope = this.closest('.product__block') || document;
      var placement = scope.querySelector('square-placement[id^="placement-"]');
      if (!placement) return;
      placement.setAttribute('data-amount', (shownTotal / 100).toFixed(2));
    }

    // Finding #1: removing one of OUR lines can shift the cart's discount state and therefore the
    // OTHER lines' keys (Shopify recomputes a line's key from its properties + whatever discount
    // now applies to it), so a key list read once up front can go stale after the very first
    // removal. Re-read /cart.js and remove ONE line by its CURRENT key each pass, bounded so a
    // persistently-failing removal can't spin forever. Returns true only once a fresh read
    // confirms every line carrying this bundleAddId is gone.
    async cleanupFailedAdd(bundleAddId) {
      var maxAttempts = 10;
      for (var attempt = 0; attempt < maxAttempts; attempt++) {
        var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return false;
        var cart = await res.json();
        var items = cart.items || [];
        var ours = items.filter((item) => String(item.properties && item.properties.__bundleaddid) === String(bundleAddId));
        if (ours.length === 0) return true;

        var changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: ours[0].key, quantity: 0 })
        });
        if (!changeRes.ok) return false;
      }
      return false; // bounded attempts exhausted; some lines may remain
    }

    // Authoritative /cart.js read, ported from native-personalizer.js's cartHasRushLine
    // (assets/native-personalizer.js:1782-1804): the "orphan rule". A rush line only counts as
    // coverage when the cart ALSO holds a different line carrying `Rush: "Yes"` — an orphaned rush
    // line (one whose rushed garment was removed) is already condemned by custom.js's legacy sweep
    // (removeRushProductFromCart, 1.5s after every theme:cart:change) and must not be trusted to
    // cover a new rushed add, or the shopper is charged nothing for a rush that reads as paid.
    // Duplicate-over-silent-and-unpaid is the same deliberate choice native-personalizer.js makes.
    async cartHasRushLine(rushProductId) {
      var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) throw new Error('cart.js read failed: ' + res.status);
      var cart = await res.json();
      var items = cart.items || [];
      // Order-level Rush (cart Rush section, 2026-09-29): a Rush line covers the order while any
      // personalised line exists; a cart-added Rush line stamps no garment. Same rule as
      // native-personalizer.js cartHasRushLine.
      var hasRushLine = items.some((item) => String(item.product_id) === String(rushProductId));
      var hasPersonalisedLine = items.some(
        (item) => String(item.product_id) !== String(rushProductId) && item.properties &&
          (String(item.properties['Embroidery text'] || '').trim() !== '' || String(item.properties.Initials || '').trim() !== '')
      );
      return hasRushLine && hasPersonalisedLine;
    }

    async onSubmit(e) {
      e.preventDefault();

      if (this._submitting) return;

      var strings = this.strings();

      // Cart-edit mode (B8/B9): the main button must not add a SECOND bundle while she is editing
      // the one already in her bag (code review, 2026-09-25). It now reads "Back to bag" (B9,
      // setBundleEditButtonText()) and does exactly what the banner's Done button does, rather than
      // blocking with an error: each tile's own drawer Confirm already saves that tile as she goes,
      // so there is nothing left for a blocking error to protect against.
      if (this._bundleEditMode) {
        this.onBundleEditDone();
        return;
      }

      if (this.anyPersonalizerEditing()) {
        this.showError(strings.midEdit);
        return;
      }

      // Engine-agnostic size validity (required radio groups) is assets/alt-bundle-picker.js's
      // rule 5 (handleSubmitReveal), bound on the SAME form with a capture-phase listener added
      // BEFORE this one (alt-bundle-picker.js loads first, see product-bundle-components.liquid).
      // Its own e.stopPropagation() does not stop this sibling target-phase listener from also
      // firing (only stopImmediatePropagation would), so this element must not attempt an add
      // against a form the browser itself considers invalid. checkValidity() reflects the SAME
      // required attributes the picker's own gate reads; this is a defensive mirror of that gate,
      // not a duplicate implementation of it (the picker still owns focusing/opening the invalid
      // tile and calling reportValidity()).
      if (this.bundleForm && !this.bundleForm.checkValidity()) return;

      var submitBtn = this.bundleForm.querySelector('.product__bundle-submit');
      this.setBusy(true, submitBtn);
      this.clearError();

      try {
        // Finding #2: resolve any add this element lost track of on a PRIOR submit (see
        // handleLostAdd() below) before building a NEW one. If that old add actually landed, this
        // stops a retry from reading as "it never happened" a second time and posting a duplicate.
        if (this._pendingBundleAddId) {
          var pending = await this.reconcileLostAdd(this._pendingBundleAddId);
          if (pending.checked) this._pendingBundleAddId = null;
        }

        var built = this.buildItems();
        var items = built.items;
        var bundleAddId = built.bundleAddId;

        if (items.length === 0) return;

        var bundleHasRush = items.some((item) => item.properties && item.properties.Rush === 'Yes');

        items.reverse(); // Same order as custom.js: last component added first.

        if (bundleHasRush) {
          var rushProduct = window.theme && window.theme.rushProduct;
          var rushVariant = rushProduct && rushProduct.variants && rushProduct.variants[0];
          if (rushVariant && rushVariant.id) {
            var covered = false;
            try {
              covered = await this.cartHasRushLine(rushProduct.id);
            } catch (rushCheckError) {
              // Fail open toward CHARGING for the rush rather than silently dropping it: an
              // unreadable pre-check must not suppress the bare rush line, or a shopper who asked
              // for rush and whose garment carries Rush: "Yes" gets no $65 line at all.
              covered = false;
            }
            if (!covered) {
              items.push({ id: rushVariant.id, quantity: 1 }); // Bare: no properties, ever.
            }
          }
        }

        var response = null;
        var transportFailed = false;
        try {
          response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: items })
          });
        } catch (networkError) {
          transportFailed = true;
        }

        // Finding #2: a network error on the POST itself. We genuinely do not know whether
        // /cart/add.js received it before the connection dropped, so this is NOT "it never
        // happened" — reconcile against /cart.js rather than guessing.
        if (transportFailed) {
          await this.handleLostAdd(bundleAddId, strings);
          return;
        }

        if (!response.ok) {
          // A REAL, KNOWN failure: the server answered, so no reconcile is needed here, we
          // already know this add did not land.
          var message = strings.errorGeneric;
          try {
            var errorBody = await response.json();
            if (errorBody && errorBody.description) message = errorBody.description;
          } catch (parseError) {
            // Keep the generic message.
          }
          this.showError(message);
          var cleanedUp = false;
          try {
            cleanedUp = await this.cleanupFailedAdd(bundleAddId);
          } catch (cleanupError) {
            // The add already failed; a failed cleanup read/write must not mask that with a
            // second, more confusing error. Logged loudly so it's not silently lost.
            console.error('[alt-bundle] cleanup after a failed add also failed', cleanupError);
          }
          if (!cleanedUp) {
            this.showError(message + ' ' + strings.cleanupIncomplete);
            console.error('[alt-bundle] cleanup after a failed add left items in the bag', bundleAddId);
          }
          return; // Do NOT open the drawer.
        }

        var data;
        try {
          data = await response.json();
        } catch (parseError) {
          // Finding #2: the POST WAS SENT and Shopify answered 2xx, so — unlike the branch above —
          // this is not a "never happened" failure either: the add may well have landed and only
          // the response body failed to parse. Same reconcile-before-guessing treatment.
          await this.handleLostAdd(bundleAddId, strings);
          return;
        }

        this.handleSuccessfulAdd(data);
      } finally {
        this.setBusy(false, submitBtn);
      }
    }

    // Finding #2. Reads /cart.js and checks for a line carrying THIS attempt's __bundleaddid.
    // Never throws: every caller treats a failed/thrown read as "still don't know", the
    // conservative direction (duplicate-over-silent-loss, same choice native-personalizer.js's own
    // cartHasRushLine-adjacent logic makes for rush coverage).
    async reconcileLostAdd(bundleAddId) {
      try {
        var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) return { checked: false, succeeded: false, cart: null };
        var cart = await res.json();
        var items = cart.items || [];
        var succeeded = items.some((item) => String(item.properties && item.properties.__bundleaddid) === String(bundleAddId));
        return { checked: true, succeeded: succeeded, cart: cart };
      } catch (error) {
        return { checked: false, succeeded: false, cart: null };
      }
    }

    // Finding #2, shared by the two ways an add can be lost: a network error on the POST itself,
    // and a 2xx response whose body failed to parse. If the add actually landed (this attempt's
    // __bundleaddid shows up on a cart line), treat it as SUCCESSFUL and run the exact same
    // success path a clean response would have — posting a SECOND add on retry is the duplicate
    // bug this exists to prevent. If the cart read confirms it did NOT land, show the network
    // error with nothing left pending. If the read itself fails too, there is genuinely no way to
    // know yet: show the network error and remember the id so the NEXT submit checks again before
    // posting a new add (see onSubmit's own pre-check above).
    async handleLostAdd(bundleAddId, strings) {
      var reconcile = await this.reconcileLostAdd(bundleAddId);
      if (reconcile.checked && reconcile.succeeded) {
        this.handleSuccessfulAdd(reconcile.cart);
        return;
      }
      if (reconcile.checked && !reconcile.succeeded) {
        this.showError(strings.errorNetwork);
        return;
      }
      this._pendingBundleAddId = bundleAddId;
      this.showError(strings.errorNetwork);
    }

    // The normal success path (drawer open + cart refresh + personalizer reset + the
    // alt-bundle:added announcement alt-bundle-picker.js listens for), factored out so a
    // RECONCILED success (finding #2, handleLostAdd()) runs through the identical code a clean
    // /cart/add.js response does.
    handleSuccessfulAdd(data) {
      var cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        cartDrawer.openCartDrawer();
        document.dispatchEvent(
          new CustomEvent('theme:cart:refresh', {
            detail: { response: data, cartDrawer: cartDrawer },
            bubbles: true
          })
        );
      } else {
        window.location.href = '/cart';
      }

      this.querySelectorAll('native-personalizer').forEach((np) => {
        if (typeof np.resetAfterBundleAdd === 'function') np.resetAfterBundleAdd();
      });

      this.dispatchEvent(new CustomEvent('alt-bundle:added', { bubbles: true, detail: { response: data } }));
    }

    // ==========================================================================================
    // B8/B9: cart "Edit personalization"/"Add personalization" link landing on the bundle PDP.
    // Lets a shopper edit, remove, or newly add the personalization on ANY component of an
    // already-in-cart bundle instance from the one link she tapped (B9, 2026-09-25: extended from
    // single-line to whole-bundle scope — the bug this fixes is that a second, previously
    // unpersonalized item personalized during a single-line edit session was never saved and the
    // main Add button was blocked, leaving her stuck). Parity with a standard product's own edit
    // link (native-personalizer.js's initEditPrefill()/replaceEditedLine()) is NOT reused here (see
    // native-personalizer.js's own bundle-mode gate on that call site) because a bundle line needs
    // three things it cannot give:
    //   1. FINGERPRINT resolution (variant id + __bundleaddid + __bundleproduct), not key-only:
    //      a cart line's key churns when the cart's discount state changes (e.g. BRIDAL-BUNDLE).
    //   2. Opening the right ACCORDION TILE before opening the drawer inside it: every tile but
    //      the initially-open one renders its panel `[hidden]`, and <dialog>.showModal() throws
    //      if any ancestor is not being rendered, not merely present in the DOM. Only the TARGET
    //      tile (the one the link named) ever gets this; every other tile is prefilled/confirmed
    //      through the same public buttons/events with its dialog never shown (confirmDrawer()'s
    //      own closeDrawer() call is a safe no-op, `if (!this.drawer.open) return;`, when the
    //      dialog was never opened, and enableBranch() — which un-disables the branch fieldset —
    //      is triggered by the branch toggle's own `change` listener regardless of drawer state;
    //      verified by reading native-personalizer.js, not assumed).
    //   3. A DIRECT /cart/change.js save on confirm/remove, never a fresh /cart/add.js + decrement
    //      pair: each bundle line already exists at the right variant and quantity, only its
    //      properties change, and a size change during the edit is explicitly out of scope.
    // Every interaction with a personalizer below goes through its PUBLIC surface only (the same
    // buttons/inputs/events a shopper's own click would use, or the bundle-mode API documented at
    // the end of native-personalizer.js), never a private method — this file's header comment
    // already documents that discipline for the add flow, and it holds here too.
    // ==========================================================================================

    initBundleEditFromUrl() {
      if (this._bundleEditInitDone) return; // one attempt per page load
      this._bundleEditInitDone = true;
      if (!window.URLSearchParams || !window.URL) return;

      var params = new URLSearchParams(window.location.search);
      var editKey = params.get('np_edit');
      var bundleLine = params.get('np_bundle_line');
      if (!editKey || !bundleLine) return; // normal bundle page (item 4): nothing to do

      var parts = bundleLine.split(':');
      if (parts.length !== 2 || !parts[0] || !parts[1]) return;
      var bundleHandle = this.dataset.bundleHandle;
      if (!bundleHandle) return;

      var target = { variantId: parts[0], bundleAddId: parts[1], bundleHandle: bundleHandle };
      this.resolveAndOpenBundleEdit(target).catch((error) => {
        console.warn('[alt-bundle] edit-link prefill aborted', error);
      });
    }

    // Fingerprint filter for the TARGET line only: variant + __bundleaddid + __bundleproduct is
    // unique by construction (bundleAddId is randomised per add in buildItems() above), so an
    // exact-one match is the success case; zero or more than one both abort the same way (item 2a:
    // "not found or ambiguous"). Reused unchanged by the save-time re-resolve (saveBundleEdit).
    findBundleLine(cartItems, target) {
      var matches = (cartItems || []).filter(
        (item) =>
          item.properties &&
          String(item.properties.__bundleproduct) === target.bundleHandle &&
          String(item.properties.__bundleaddid) === target.bundleAddId &&
          String(item.variant_id) === String(target.variantId)
      );
      return matches.length === 1 ? matches[0] : null;
    }

    // B9: whole-bundle resolve. Maps EVERY line of this bundle instance (same __bundleaddid +
    // __bundleproduct as the link's target) to its tile, sets every mapped tile's size, and
    // prefills+auto-confirms every PERSONALIZED mapped line — opening the accordion/drawer only for
    // the target tile (see the class header above for why that's safe for the others too). A tile
    // whose line fails validation is marked individually (markTileEditUnavailable()) and the loop
    // continues; only a genuinely unresolvable TARGET aborts the whole thing, matching the original
    // not-found contract.
    async resolveAndOpenBundleEdit(target) {
      var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      var cart = await res.json();

      var targetLine = this.findBundleLine(cart.items, target);
      if (!targetLine) {
        this.showEditNotice(this.strings().editNotFound);
        this.stripEditParams();
        return;
      }

      var instanceLines = (cart.items || []).filter(
        (item) =>
          item.properties &&
          String(item.properties.__bundleproduct) === target.bundleHandle &&
          String(item.properties.__bundleaddid) === target.bundleAddId
      );

      // Map every tile whose component JSON claims one of this instance's line variants (variant
      // ids are globally unique per product, so this is unambiguous regardless of tile type).
      // Tiles with no matching line (should not happen: every component appears once per bundle
      // add) are simply never pushed here — left untouched and read-only.
      var tiles = Array.prototype.slice.call(this.querySelectorAll('bundle-component'));
      var mapped = [];
      tiles.forEach((tile) => {
        var data = componentData(tile);
        if (!data || !data.variants) return;
        var line = instanceLines.find((item) => data.variants.some((v) => String(v.id) === String(item.variant_id)));
        if (!line) return;
        var variant = data.variants.find((v) => String(v.id) === String(line.variant_id));
        mapped.push({ tile: tile, variant: variant, line: line, isTarget: String(line.variant_id) === String(target.variantId) });
      });

      var targetEntry = mapped.find((m) => m.isTarget);
      var targetPersonalizer = targetEntry ? targetEntry.tile.querySelector('native-personalizer') : null;
      if (!targetEntry || !targetPersonalizer) {
        // Either the target line resolved above but no tile's component JSON claims its variant,
        // or that tile carries no personalizer at all: both are the same "not found" the single-
        // line version reported for a malformed link.
        this.showEditNotice(this.strings().editNotFound);
        this.stripEditParams();
        return;
      }

      var editedTiles = [];

      for (var i = 0; i < mapped.length; i += 1) {
        var entry = mapped[i];

        // Rule 1: every mapped tile's size is set/locked before anything else, personalized or
        // not — dispatches alt-bundle:variant-change same as a real click, but armBundleEditMode()
        // below binds the size-lock listener only AFTER this loop finishes, so none of these
        // programmatic sets can trigger their own "size locked" warning.
        this.setTileVariant(entry.tile, entry.variant);
        var personalizer = entry.tile.querySelector('native-personalizer');
        editedTiles.push({
          tile: entry.tile,
          personalizer: personalizer || null,
          variantId: entry.variant.id,
          bundleAddId: target.bundleAddId,
          bundleHandle: target.bundleHandle
        });

        var props = entry.line.properties || {};
        var has = (k) => Object.prototype.hasOwnProperty.call(props, k);
        var hasText = has('Embroidery text');
        var hasInitials = has('Initials');

        if (!hasText && !hasInitials) {
          // Not personalized. Nothing to prefill — but if this is the TARGET (she tapped "Add
          // personalization" on a previously-unpersonalized item), her drawer still opens empty,
          // ready to type (item 4).
          if (entry.isTarget && personalizer) {
            var addHead = entry.tile.querySelector('.alt-bundle-tile__head');
            if (addHead && typeof addHead.click === 'function') addHead.click();
            var addOpenTrigger = personalizer.querySelector('[data-np-open]') || personalizer.querySelector('[data-np-edit]');
            if (addOpenTrigger && typeof addOpenTrigger.click === 'function') addOpenTrigger.click();
          }
          continue;
        }

        if (!personalizer || hasText === hasInitials) {
          // No personalizer on a tile whose line claims personalization, or both keys present
          // (malformed/ambiguous contract line): mark this tile only, keep going.
          this.markTileEditUnavailable(entry.tile);
          continue;
        }

        // Finding #8: an un-upgraded <native-personalizer> is a plain, inert HTMLElement — its
        // connectedCallback (which wires every button below) has not run yet. Bounded wait, same
        // fail-open discipline as every other guard in this function.
        var upgraded = await this.waitForPersonalizerUpgrade(personalizer, 8000);
        if (!upgraded) {
          this.markTileEditUnavailable(entry.tile);
          continue;
        }

        // The branch mapping lives in native-personalizer.js (one descriptor table, shared with the
        // standalone cart-edit prefill): `Personalization Type` names the branch when present
        // (Standard, Initials or MRS. Surname, the only way to tell Standard from Surname since both
        // write `Embroidery text`), else the old key-based inference. A line the mapping can not
        // represent in THIS tile (unknown type, missing key, text over the limit, a font or thread
        // colour the config does not list) fails safe exactly as before: read-only tile, edit
        // unavailable.
        var resolved = typeof personalizer.resolveEditLine === 'function' ? personalizer.resolveEditLine(props) : null;
        var controls = resolved && typeof personalizer.editControls === 'function' ? personalizer.editControls(resolved) : null;
        if (!resolved || !controls) {
          this.markTileEditUnavailable(entry.tile);
          continue;
        }
        var branch = resolved.branch;
        var text = resolved.text;
        var input = controls.input;
        var fontRadio = controls.fontRadio;
        var colorRadio = controls.colorRadio;

        // Only the target tile's accordion/drawer actually opens (see the class header comment
        // for why every other tile's confirm below is safe without it).
        if (entry.isTarget) {
          var head = entry.tile.querySelector('.alt-bundle-tile__head');
          if (head && typeof head.click === 'function') head.click();
          var openTrigger = personalizer.querySelector('[data-np-open]') || personalizer.querySelector('[data-np-edit]');
          if (openTrigger && typeof openTrigger.click === 'function') openTrigger.click();
          var drawer = personalizer.querySelector('[data-np-drawer]');
          if (!drawer || !drawer.open) {
            this.markTileEditUnavailable(entry.tile);
            continue;
          }
        }

        var toggle = Array.prototype.find.call(personalizer.querySelectorAll('[data-np-branch-toggle]'), (t) => t.value === branch);
        if (toggle && !toggle.checked) {
          toggle.checked = true;
          toggle.dispatchEvent(new Event('change', { bubbles: true }));
        }

        input.value = text;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        if (fontRadio && !fontRadio.checked) {
          fontRadio.checked = true;
          fontRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }
        if (colorRadio && !colorRadio.checked) {
          colorRadio.checked = true;
          colorRadio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Auto-confirm the ORIGINAL (pre-edit) values, same reasoning the single-line version
        // documented: this is what makes "the drawer is confirmed" and "its × remove is used" two
        // parallel first actions rather than a forced confirm-then-remove sequence. Reuses
        // confirmDrawer() through its own public button, never called directly.
        var confirmBtn = personalizer.querySelector('[data-np-drawer-confirm]');
        if (!confirmBtn || typeof confirmBtn.click !== 'function') {
          this.markTileEditUnavailable(entry.tile);
          continue;
        }
        confirmBtn.click();
        var confirmedPanel = personalizer.querySelector('[data-np-panel]');
        if (!confirmedPanel || confirmedPanel.hidden !== false) {
          this.markTileEditUnavailable(entry.tile);
          continue;
        }

        if (entry.isTarget) {
          // She arrived by tapping the link, so land her IN the drawer, ready to type
          // (orchestrator review, 2026-09-24). The pencil only reopens the drawer prefilled with
          // the confirmed values; it is not a save trigger, so the save still only happens when
          // she confirms or removes.
          var editBtn = personalizer.querySelector('[data-np-edit]');
          if (editBtn && typeof editBtn.click === 'function') editBtn.click();
        }
      }

      this.armBundleEditMode({ bundleAddId: target.bundleAddId, bundleHandle: target.bundleHandle, tiles: editedTiles });
      this.stripEditParams();
    }

    // B9 per-tile fallback (item 1: "other tiles still work"): a validation mismatch on ONE mapped
    // line no longer aborts the whole prefill. Reuses the existing editUnavailable string (same
    // message the single-line version showed) rather than a new locale key, just scoped to this
    // one tile's own notice element (snippets/product-bundle-components.liquid).
    markTileEditUnavailable(tile) {
      var notice = tile.querySelector('[data-alt-tile-edit-notice]');
      if (!notice) return;
      notice.textContent = this.strings().editUnavailable;
      notice.hidden = false;
    }

    // Finding #8. Bounded wait for `whenDefined()` PLUS one animation frame: whenDefined() alone
    // only promises the class is REGISTERED; for an element already in the DOM when define() runs
    // (true here — this page is server-rendered before any deferred script executes), the spec
    // upgrades it SYNCHRONOUSLY as part of that same define() call, so by the time whenDefined()
    // resolves this element's connectedCallback has already run. The extra frame is
    // defence-in-depth against any upgrade-adjacent layout work not yet settled before this
    // function starts calling .click() on the personalizer's own buttons. Resolves false on
    // timeout, or true immediately if the element is already upgraded (isEditing is on the
    // prototype, so its presence IS the upgrade signal) — never throws, matching every other guard
    // in this function's fail-open discipline.
    waitForPersonalizerUpgrade(personalizer, timeoutMs) {
      if (typeof personalizer.isEditing === 'function') return Promise.resolve(true);
      if (!window.customElements || typeof customElements.whenDefined !== 'function') {
        return Promise.resolve(false);
      }
      var whenReady = customElements.whenDefined('native-personalizer').then(function () {
        return new Promise((resolve) => {
          if (window.requestAnimationFrame) {
            requestAnimationFrame(() => resolve(true));
          } else {
            setTimeout(() => resolve(true), 0);
          }
        });
      });
      var timeout = new Promise((resolve) => setTimeout(() => resolve(false), timeoutMs));
      return Promise.race([whenReady, timeout]);
    }

    // Mirrors bindSizeWiring()'s own click-handler side effects exactly (same file, so this is
    // reusing our own logic, never reaching into native-personalizer.js or custom.js): sets the
    // selected-variant attributes, mirrors the legend text, checks the matching size radio (if
    // the option isn't disabled/sold-out; a since-sold-out size the shopper already owns still
    // gets addressed correctly, it just can't be re-selected by hand), and dispatches
    // alt-bundle:variant-change so native-personalizer.js's onBundleVariantChange (availability)
    // reacts the same way it would to a real click.
    setTileVariant(tile, variant) {
      tile.setAttribute('data-selected-variant-id', variant.id);
      tile.setAttribute('data-selected-variant-available', String(!!variant.available));
      var legendValue = tile.querySelector('.radio__legend__value');
      if (legendValue) legendValue.innerText = variant.title;
      var radios = tile.querySelectorAll('[data-single-option-selector]');
      var radio = Array.prototype.find.call(radios, (r) => r.value === variant.title);
      if (radio) radio.checked = true;
      tile.dispatchEvent(new CustomEvent('alt-bundle:variant-change', { bubbles: true, detail: { variant: variant } }));
    }

    // B9: `edit` = { bundleAddId, bundleHandle, tiles }, tiles an array of { tile, personalizer
    // (null for a mapped tile with no personalizer at all), variantId (this line's ORIGINAL
    // variant — size changes during the edit are out of scope, saveBundleEdit() always re-resolves
    // against this, never the tile's current selection), bundleAddId, bundleHandle }, one entry per
    // tile resolveAndOpenBundleEdit() mapped to a cart line, built regardless of whether that line
    // is currently personalized: rule 1 locks every mapped tile's size for the whole session, and
    // an unpersonalized-but-personalizable tile must still be able to trigger its OWN save the
    // first time she confirms it — that gap (no listener anywhere for a tile outside the single
    // original target) was the whole-bundle bug this replaces the single-tile version to fix.
    armBundleEditMode(edit) {
      this._bundleEditMode = edit;
      this.showEditBanner(this.strings().editEditing);
      this.setBundleEditButtonText(true);

      // One shared handler for every tile (refreshSizeLockBanner() just re-scans
      // this._bundleEditMode.tiles on any change, so it needs no per-tile closure). Armed only
      // now, AFTER the resolve loop's own setTileVariant() calls already dispatched their
      // programmatic alt-bundle:variant-change, so none of those self-trigger the lock message.
      this._onBundleEditSizeChange = this.refreshSizeLockBanner.bind(this);

      edit.tiles.forEach((t) => {
        t.tile.addEventListener('alt-bundle:variant-change', this._onBundleEditSizeChange);
        if (!t.personalizer) return; // locked-size only; no confirm/remove event to ever save
        // Findings #3/#4, per tile: np:bundle-confirmed/np:bundle-removed fire once
        // confirmDrawer()/removePersonalization() have ALREADY committed panel.hidden/
        // lastConfirmed (bundle mode, native-personalizer.js), so no setTimeout margin is needed
        // and a Confirm triggered by Enter (which never clicks the button) is still caught.
        t._onSaveTrigger = () => this.requestBundleEditSave(t);
        t.tile.addEventListener('np:bundle-confirmed', t._onSaveTrigger);
        t.tile.addEventListener('np:bundle-removed', t._onSaveTrigger);
      });

      var banner = this.querySelector('[data-alt-edit-banner]');
      var doneBtn = banner ? banner.querySelector('[data-alt-edit-banner-cancel]') : null;
      if (doneBtn) {
        this._onBundleEditDone = this.onBundleEditDone.bind(this);
        doneBtn.addEventListener('click', this._onBundleEditDone);
      }
    }

    // Item 3 (size-locked message), generalized across every mapped tile: shows the locked notice
    // while ANY mapped tile's current selection differs from the variant it held when the edit
    // session started, restores the normal editing banner once every tile is back to its original
    // size. A "Saved to your bag." confirmation (showSavedConfirmation()) briefly owns the same
    // banner element on its own token-guarded timer; a stray size click during that ~3s window can
    // lose the lock warning until the confirmation's own restore runs, an accepted low-probability
    // trade-off rather than layering a second precedence system onto one text element.
    refreshSizeLockBanner() {
      if (!this._bundleEditMode) return;
      var mismatched = this._bundleEditMode.tiles.some(
        (t) => String(t.tile.getAttribute('data-selected-variant-id')) !== String(t.variantId)
      );
      this.showEditBanner(mismatched ? this.strings().editSizeLocked : this.strings().editEditing);
    }

    // Finding #3, generalized (B9): a Map<tile, target> instead of one pending target, so saves
    // never run concurrently across TILES either (never just within one). A second confirm/remove
    // on the SAME tile while a save is in flight overwrites that tile's queued entry (latest state
    // of that tile wins); a confirm on a DIFFERENT tile queues its own entry and is never dropped.
    requestBundleEditSave(target) {
      if (!this._bundleEditPending) this._bundleEditPending = new Map();
      this._bundleEditPending.set(target.tile, target);
      if (this._bundleEditSaveInFlight) return;
      this.runBundleEditSave();
    }

    // Drains the pending map one entry at a time (insertion order), never two at once. Each save
    // reads getConfirmedProperties() fresh off the live DOM at the moment IT runs
    // (saveBundleEdit()), so a tile queued twice before its turn simply reflects whatever is
    // confirmed by the time it's dequeued, never a stale snapshot. Edit mode itself is never
    // cleared here (B9: it now survives across saves; only Done/Back to bag via
    // onBundleEditDone() ends it).
    async runBundleEditSave() {
      if (!this._bundleEditPending || this._bundleEditPending.size === 0) return;
      this._bundleEditSaveInFlight = true;

      var next = this._bundleEditPending.entries().next().value;
      var tile = next[0];
      var target = next[1];
      this._bundleEditPending.delete(tile);

      var result = { succeeded: false, rushRemovalFailed: false };
      try {
        result = await this.saveBundleEdit(target);
      } finally {
        this._bundleEditSaveInFlight = false;
      }

      if (result.succeeded && this._bundleEditMode) {
        this.showSavedConfirmation();
        if (result.rushRemovalFailed) this.showError(this.strings().rushRemovalFailed);
      }

      if (this._bundleEditMode && this._bundleEditPending && this._bundleEditPending.size > 0) {
        await this.runBundleEditSave();
      }
    }

    // B9 (renamed from onBundleEditCancel): the banner button now reads "Done" and the main
    // submit/sticky-bar buttons read "Back to bag" and route here too (onSubmit's own
    // _bundleEditMode guard). Cancels any tile still mid-edit (a drawer left open with an
    // unconfirmed draft) across every mapped personalizable tile, same safety net the single-tile
    // version had for its one target. Ends the session and opens the cart drawer so she sees
    // everything she saved (never popped per-save any more; see saveBundleEdit()'s own comment).
    onBundleEditDone() {
      if (!this._bundleEditMode) return;
      this._bundleEditMode.tiles.forEach((t) => {
        if (!t.personalizer) return;
        var drawer = t.personalizer.querySelector('[data-np-drawer]');
        var cancelBtn = t.personalizer.querySelector('[data-np-drawer-cancel]');
        if (drawer && drawer.open && cancelBtn) cancelBtn.click();
      });
      this.clearBundleEditMode();
      this.stripEditParams();
      this.openCartDrawerForDone();
    }

    // Read-only: fetches /cart.js so the drawer reflects the true current state even if she hit
    // Done without ever confirming a save this session, then opens it. No cart write happens here.
    openCartDrawerForDone() {
      var cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) {
        window.location.href = '/cart';
        return;
      }
      cartDrawer.openCartDrawer();
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          document.dispatchEvent(
            new CustomEvent('theme:cart:refresh', { detail: { response: data, cartDrawer: cartDrawer }, bubbles: true })
          );
        })
        .catch(() => {
          // Best-effort only: the drawer is already open showing whatever it last had, which is
          // still correct if every save this session already dispatched its own refresh.
        });
    }

    clearBundleEditMode() {
      if (!this._bundleEditMode) return;
      this._bundleEditMode.tiles.forEach((t) => {
        if (this._onBundleEditSizeChange) t.tile.removeEventListener('alt-bundle:variant-change', this._onBundleEditSizeChange);
        if (t._onSaveTrigger) {
          t.tile.removeEventListener('np:bundle-confirmed', t._onSaveTrigger);
          t.tile.removeEventListener('np:bundle-removed', t._onSaveTrigger);
        }
      });
      var banner = this.querySelector('[data-alt-edit-banner]');
      var doneBtn = banner ? banner.querySelector('[data-alt-edit-banner-cancel]') : null;
      if (doneBtn && this._onBundleEditDone) doneBtn.removeEventListener('click', this._onBundleEditDone);
      this._bundleEditMode = null;
      this._bundleEditPending = null;
      this.setBundleEditButtonText(false);
      this.hideEditBanner();
      this.clearError();
    }

    // Item 3's per-save feedback: swap the banner text for ~3s, then restore whatever the banner
    // should show right now (refreshSizeLockBanner(), not a hardcoded "editing" string, so a save
    // landing while a size mismatch is showing restores to THAT, not a false all-clear).
    // Token-guarded so two saves within 3s of each other never let the earlier one's timer stomp
    // the later one's still-displayed confirmation.
    showSavedConfirmation() {
      this._bundleEditSavedToken = (this._bundleEditSavedToken || 0) + 1;
      var token = this._bundleEditSavedToken;
      this.showEditBanner(this.strings().savedConfirmation);
      setTimeout(() => {
        if (!this._bundleEditMode || this._bundleEditSavedToken !== token) return;
        this.refreshSizeLockBanner();
      }, 3000);
    }

    // Item 3 (Back to bag / sticky bar mirror). Overwrites data-alt-original-text rather than
    // fighting alt-bundle-picker.js's own textContent writes directly: updateBundleCountUI() there
    // falls back to THIS attribute whenever it has no "choose N more sizes" guidance to show
    // (always true during an edit session, since every mapped tile's size is already set), so once
    // we own that attribute the picker's own refreshes can never stomp our text back to "Add to
    // Bag". Restores the TRUE original (stashed once, never overwritten again) when edit mode ends.
    setBundleEditButtonText(entering) {
      var submitBtn = this.bundleForm ? this.bundleForm.querySelector('.product__bundle-submit') : null;
      var barText =
        document.querySelector('#cart-bar [data-add-to-cart-bar] [data-add-to-cart-text]') ||
        document.querySelector('#cart-bar [data-add-to-cart-bar] .btn__text');
      var els = [submitBtn, barText].filter(Boolean);

      if (entering) {
        var text = this.strings().backToBag;
        els.forEach((el) => {
          if (!el.hasAttribute('data-alt-bundle-edit-orig-text')) {
            el.setAttribute('data-alt-bundle-edit-orig-text', el.getAttribute('data-alt-original-text') || el.textContent);
          }
          el.setAttribute('data-alt-original-text', text);
          el.textContent = text;
        });
        return;
      }

      els.forEach((el) => {
        var orig = el.getAttribute('data-alt-bundle-edit-orig-text');
        if (orig == null) return;
        el.setAttribute('data-alt-original-text', orig);
        el.textContent = orig;
        el.removeAttribute('data-alt-bundle-edit-orig-text');
      });
    }

    showEditBanner(text) {
      var banner = this.querySelector('[data-alt-edit-banner]');
      if (!banner) return;
      banner.removeAttribute('data-alt-edit-notice');
      var textEl = banner.querySelector('[data-alt-edit-banner-text]');
      if (textEl) textEl.textContent = text;
      banner.hidden = false;
    }

    // Not-found / validation-failed abort: same banner element, marked data-alt-edit-notice so
    // the CSS hides the Cancel button (there is nothing left to confirm or cancel).
    showEditNotice(text) {
      var banner = this.querySelector('[data-alt-edit-banner]');
      if (!banner) return;
      banner.setAttribute('data-alt-edit-notice', '');
      var textEl = banner.querySelector('[data-alt-edit-banner-text]');
      if (textEl) textEl.textContent = text;
      banner.hidden = false;
    }

    hideEditBanner() {
      var banner = this.querySelector('[data-alt-edit-banner]');
      if (!banner) return;
      banner.hidden = true;
      banner.removeAttribute('data-alt-edit-notice');
    }

    // Consume the params only once we've committed to a state (a successful prefill, or an
    // aborted one that already showed its notice): a refresh must not re-trigger against the
    // same params. Surgical delete, same as native-personalizer.js's own initEditPrefill():
    // variant, view, preview_theme_id and hash all survive.
    stripEditParams() {
      if (!window.URL) return;
      var url = new URL(window.location.href);
      url.searchParams.delete('np_edit');
      url.searchParams.delete('np_bundle_line');
      window.history.replaceState(window.history.state, '', url.toString());
    }

    // Finding #3: no longer clears edit mode or decides success/failure UI itself — it returns
    // { succeeded, rushRemovalFailed } and lets runBundleEditSave() (above) decide, so a queued
    // follow-up save can run before edit mode is torn down. Finding #9: `succeeded: true` even
    // when the trailing rush cleanup fails, because the PERSONALIZATION edit itself (the
    // /cart/change.js call just above it) already landed — only the orphaned-rush cleanup did not.
    // B9: `target` is one tile's { tile, personalizer, variantId, bundleAddId, bundleHandle } from
    // armBundleEditMode()'s tiles array (findBundleLine() re-resolves the CURRENT key by this exact
    // fingerprint, so a discount-state churn since resolveAndOpenBundleEdit() never breaks the
    // save). Rush is now decided against the WHOLE bundle instance, not just this one line: a line
    // that becomes personalized gets Rush only if the bundle already carries it somewhere (never
    // newly introduced), and a line that keeps personalization keeps its own value, both covered by
    // the same `stillPersonalized && bundleHadRush` test since a line's own prior Rush already
    // counts toward bundleHadRush. Per-save, the cart drawer is deliberately NOT opened any more
    // (item 3, orchestrator sign-off 2026-09-25): only a `theme:cart:refresh` dispatch, so header/
    // mini-cart badges stay live without interrupting her mid-session on another tile; the drawer
    // opens only once, on Done/Back to bag (onBundleEditDone -> openCartDrawerForDone()).
    async saveBundleEdit(target) {
      this.clearError();
      var confirmed = {};
      if (target.personalizer && typeof target.personalizer.getConfirmedProperties === 'function') {
        confirmed = target.personalizer.getConfirmedProperties() || {};
      }

      try {
        var res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
        if (!res.ok) throw new Error('cart.js read failed: ' + res.status);
        var cart = await res.json();
        var line = this.findBundleLine(cart.items, target);
        if (!line) {
          // The line vanished between prefill and save (another tab, a sweep): stay in edit
          // mode and tell the shopper, rather than silently posting a change against nothing.
          this.showError(this.strings().editSaveError);
          return { succeeded: false, rushRemovalFailed: false };
        }

        var oldProps = line.properties || {};
        var lineHadRush = oldProps.Rush === 'Yes';
        var stillPersonalized = Object.keys(confirmed).length > 0;

        var bundleHadRush = (cart.items || []).some(
          (item) =>
            item.properties &&
            String(item.properties.__bundleproduct) === target.bundleHandle &&
            String(item.properties.__bundleaddid) === target.bundleAddId &&
            item.properties.Rush === 'Yes'
        );

        // All existing __bundle* keys carry over unchanged (including __bundleengine and the
        // __bundleaddid that keeps this line's fingerprint unique), Rush only when the bundle
        // instance already carries it somewhere AND personalization survives this edit, then the
        // new confirmed personalization properties (none at all on a remove). Shopify replaces the
        // properties object wholesale, so any key left out (old embroidery keys on a font/colour
        // change, everything on a remove) is what actually drops them.
        var newProperties = {};
        Object.keys(oldProps).forEach((k) => {
          if (k.indexOf('__bundle') === 0) newProperties[k] = oldProps[k];
        });
        if (stillPersonalized && bundleHadRush) newProperties.Rush = 'Yes';
        Object.keys(confirmed).forEach((k) => {
          newProperties[k] = confirmed[k];
        });

        var changeRes = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties: newProperties })
        });
        if (!changeRes.ok) {
          this.showError(this.strings().editSaveError);
          return { succeeded: false, rushRemovalFailed: false }; // stay in edit mode
        }
        var updated = await changeRes.json();

        // Finding #9: check the removal actually succeeded instead of assuming it did. Only a
        // line that ITSELF had Rush and lost personalization can orphan the bare fee line (per the
        // rule above, a line can never gain Rush while the bundle had none, so there is no other
        // direction that removes the bundle's last Rush: "Yes").
        var rushRemovalFailed = false;
        if (lineHadRush && !stillPersonalized) {
          rushRemovalFailed = !(await this.dropOrphanedBareRush(updated));
        }

        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { detail: { response: updated }, bubbles: true }));

        return { succeeded: true, rushRemovalFailed: rushRemovalFailed };
      } catch (error) {
        console.error('[alt-bundle] edit save failed; the cart line is unchanged', error);
        this.showError(this.strings().editSaveError);
        return { succeeded: false, rushRemovalFailed: false };
      }
    }

    // Drops the bare rush line only when NO line in the cart still carries Rush: "Yes" after this
    // edit removed it from the one line that had it (a de-personalizing edit never adds Rush, so
    // this is the only direction that can orphan the fee line). Reads the /cart/change.js
    // response passed in, never a fresh /cart.js: that response already reflects the edit that
    // just landed. Finding #9: returns whether the removal actually succeeded (or there was
    // nothing to remove) instead of assuming success on a fetch that resolved but failed.
    async dropOrphanedBareRush(cart) {
      var items = cart.items || [];
      var rushProduct = window.theme && window.theme.rushProduct;
      // Order-level Rush: keep the Rush line while any personalised line remains, stamped or not.
      var stillPersonalised = items.some(
        (item) => (!rushProduct || String(item.product_id) !== String(rushProduct.id)) && item.properties &&
          (String(item.properties['Embroidery text'] || '').trim() !== '' || String(item.properties.Initials || '').trim() !== '')
      );
      if (stillPersonalised) return true; // nothing to drop
      var rushVariant = rushProduct && rushProduct.variants && rushProduct.variants[0];
      if (!rushVariant) return true; // no rush product configured: nothing to drop
      var rushLine = items.find((item) => String(item.variant_id) === String(rushVariant.id));
      if (!rushLine) return true; // no bare rush line present: nothing to drop
      try {
        var res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: rushLine.key, quantity: 0 })
        });
        return res.ok;
      } catch (error) {
        return false;
      }
    }
  }

  customElements.define('alt-bundle-product', AltBundleProduct);
})();
